import { useState, useEffect, useMemo } from 'react';
import { Combobox, type ComboboxOption } from "@/components/ui/combobox";
import { getBrandProfiles } from "@/api/brands";
import { getWritingStyles } from "@/api/writingStyles";
import { Asterisk, Loader2 } from "lucide-react";
import type { BrandProfile, PersonalWritingStyle } from "@/types";

interface WritingStyleSelectorProps {
  value?: string;
  onValueChange: (value: string, type: 'brand' | 'personal' | null, id: string) => void;
  placeholder?: string;
  className?: string;
}

export function WritingStyleSelector({
  value,
  onValueChange,
  placeholder = "Select a writing style...",
  className
}: WritingStyleSelectorProps) {
  const [brandProfiles, setBrandProfiles] = useState<BrandProfile[]>([]);
  const [writingStyles, setWritingStyles] = useState<PersonalWritingStyle[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setIsLoading(true);
    try {
      const [brands, styles] = await Promise.all([
        getBrandProfiles(),
        getWritingStyles()
      ]);
      setBrandProfiles(brands);
      setWritingStyles(styles);
    } catch (error) {
      console.error('Failed to load writing styles:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const profileOptions: ComboboxOption[] = useMemo(() => {
    const options: ComboboxOption[] = [];

    brandProfiles.forEach(brand => {
      options.push({
        value: `brand:${brand.id}`,
        label: brand.name,
        group: 'Brand Profiles'
      });
    });

    writingStyles.forEach(style => {
      options.push({
        value: `style:${style.id}`,
        label: style.name,
        group: 'Personal Styles'
      });
    });

    return options;
  }, [brandProfiles, writingStyles]);

  const handleChange = (newValue: string) => {
    if (!newValue) {
      onValueChange('', null, '');
      return;
    }

    if (newValue.startsWith('brand:')) {
      const id = newValue.replace('brand:', '');
      onValueChange(newValue, 'brand', id);
    } else if (newValue.startsWith('style:')) {
      const id = newValue.replace('style:', '');
      onValueChange(newValue, 'personal', id);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-4">
        <Asterisk className="h-5 w-5 animate-spin" />
      </div>
    );
  }

  if (profileOptions.length === 0) {
    return (
      <p className="text-sm text-muted-foreground text-center py-4">
        No writing styles found. Create a brand profile or personal style first.
      </p>
    );
  }

  return (
    <Combobox
      options={profileOptions}
      value={value}
      onValueChange={handleChange}
      placeholder={placeholder}
      emptyText="No writing styles found."
      searchPlaceholder="Search writing styles..."
      className={className}
    />
  );
}
